import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { formatCurrency } from "@/lib/utils";
import { Zap, Lock, Check } from "lucide-react";

export default function StrategicSection({ decision, updateDecision, team, game }) {
  const { t } = useTranslation();
  const [strategies, setStrategies] = useState([]);

  useEffect(() => {
    axios.get(`${API}/reference/strategic-decisions`).then(res => setStrategies(res.data)).catch(() => {});
  }, []);

  const unlocked = team?.strategic_unlocked || [];
  const current = decision.strategic_decision;
  const quarter = game?.current_quarter || 1;
  const cash = team?.cash || 0;
  const ownedAircraft = (team?.aircraft || []).filter(a => a.owned).length;
  const quality = team?.quality_rating || 58;
  const hasWidebody = (team?.aircraft || []).some(a => a.type === "widebody");

  const canUnlock = (sd) => {
    if (unlocked.includes(sd.id)) return { can: false, reason: "Already activated" };
    const cond = sd.unlock_condition;
    if (cond === "always") return { can: true };
    if (cond.includes("quarter")) {
      const req = parseInt(cond.split(">=")[1]);
      if (quarter < req) return { can: false, reason: `Available from Q${req}` };
    }
    if (cond.includes("cash")) {
      const req = parseInt(cond.split(">=")[1].replace(/_/g, ""));
      if (cash < req) return { can: false, reason: `Requires cash >= ${formatCurrency(req)}` };
    }
    if (cond.includes("owned_aircraft")) {
      const req = parseInt(cond.split(">=")[1]);
      if (ownedAircraft < req) return { can: false, reason: `Requires ${req}+ owned aircraft` };
    }
    if (cond.includes("quality_rating")) {
      const req = parseInt(cond.split(">=")[1]);
      if (quality < req) return { can: false, reason: `Requires quality >= ${req}` };
    }
    if (cond.includes("has_widebody")) {
      if (!hasWidebody) return { can: false, reason: "Requires a wide-body aircraft" };
    }
    return { can: true };
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="card-header-flight-deck">
          <CardTitle className="flex items-center gap-2"><Zap className="w-5 h-5" /> {t("decisions.strategicDecisions")}</CardTitle>
          <CardDescription>{t("decisions.strategicDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Current selection */}
          <div className="mb-4">
            <button onClick={() => updateDecision("strategic_decision", null)} className={`w-full p-3 text-left border-2 rounded-lg transition-colors mb-2 ${!current ? "border-sky-500 bg-sky-50" : "border-slate-200 hover:border-slate-300"}`} data-testid="strategic-none">
              <span className="font-medium text-sm">No new strategic decision this quarter</span>
            </button>
          </div>

          <div className="space-y-3">
            {strategies.map(sd => {
              const isUnlocked = unlocked.includes(sd.id);
              const { can, reason } = canUnlock(sd);
              const isSelected = current === sd.id;
              return (
                <button key={sd.id} onClick={() => can && !isUnlocked ? updateDecision("strategic_decision", isSelected ? null : sd.id) : null}
                  className={`w-full p-4 text-left border-2 rounded-lg transition-colors ${
                    isUnlocked ? "border-emerald-300 bg-emerald-50 cursor-default" :
                    isSelected ? "border-sky-500 bg-sky-50" :
                    !can ? "border-slate-100 bg-slate-50 opacity-60 cursor-not-allowed" :
                    "border-slate-200 hover:border-sky-300"
                  }`} data-testid={`strategic-${sd.id}`} disabled={!can || isUnlocked}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {isUnlocked ? <Check className="w-4 h-4 text-emerald-600" /> : !can ? <Lock className="w-4 h-4 text-slate-400" /> : null}
                      <span className="font-semibold text-sm">{sd.name}</span>
                    </div>
                    <span className="font-mono text-xs text-slate-600">{formatCurrency(sd.cost)}{sd.recurring_cost > 0 ? ` + ${formatCurrency(sd.recurring_cost)}/qtr` : ""}</span>
                  </div>
                  <p className="text-xs text-slate-600">{sd.effect}</p>
                  {!can && reason && <p className="text-[10px] text-rose-500 mt-1">{reason}</p>}
                  {isUnlocked && <p className="text-[10px] text-emerald-600 mt-1">Active - recurring costs applied automatically</p>}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
